import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AddressAttributes, Country } from '@queries/generated';
import { AddressService } from '@services/address.service';
import { pluck } from '@util/pluck';
import { CAN_POSTAL, US_ZIPCODE } from '@util/regex';
import { Dropdown } from 'primeng/dropdown';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'vnm-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AddressFormComponent,
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: AddressFormComponent,
      multi: true
    }
  ]
})
export class AddressFormComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
  @ViewChild('countrySelect', { static: true }) countrySelect: Dropdown;
  @ViewChild('provinceSelect', { static: true }) provinceSelect: Dropdown;

  @Input() addressRequired: boolean;

  addressForm: UntypedFormGroup;

  _countries: Country[] = [];
  selectedCountry: any;
  selectedProvince: any;

  constructor(private fb: UntypedFormBuilder, private addressService: AddressService) {}

  get countries(): Country[] {
    return this._countries;
  }

  get hasCountries(): boolean {
    return this.countries.length > 0;
  }

  ngOnInit() {
    const validator = this.addressRequired ? Validators.required : null;
    this.addressForm = this.fb.group({
      address1: ['', validator],
      address2: [''],
      city: ['', validator],
      postalCode: ['', validator],
      country: ['', validator],
      province: ['', validator]
    });

    this.addressService.countries$.pipe(untilDestroyed(this), take(1)).subscribe((countries) => {
      this._countries = countries;
      this.updateCountryAndProvince();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.addressRequired) {
      this.setValidators();
    }
  }

  onCountryChange(event) {
    this.selectedProvince = event.value.provinces?.[0];
    this.addressForm.patchValue({
      country: { code: event.value.code },
      province: { code: this.selectedProvince?.code }
    });
    this.updatePostalValidators(event.value.code);
  }

  onProvinceChange(event) {
    this.addressForm.patchValue({ province: { code: event.value.code } });
  }

  updatePostalValidators(code?: string) {
    let validators = [];
    const { postalCode } = this.addressForm.controls;
    switch (code) {
      case 'US':
        validators.push(Validators.pattern(US_ZIPCODE));
        break;
      case 'CA':
        validators.push(Validators.pattern(CAN_POSTAL));
        break;
    }

    if (this.addressRequired) {
      validators.push(Validators.required);
    }

    postalCode.setValidators(validators);
    postalCode.updateValueAndValidity();
  }

  updateCountryAndProvince() {
    if (this.addressForm.value.country && this.hasCountries) {
      this.selectedCountry = this.countries.find((country) => country.code === this.addressForm.value.country.code);
      if (this.selectedCountry) {
        this.selectedProvince = this.selectedCountry.provinces.find(
          (province) => province.code === this.addressForm.value.province.code
        );
      }
    } else {
      this.countrySelect.value = this.selectedCountry = null;
      this.provinceSelect.value = this.selectedProvince = null;
    }
    this.updatePostalValidators(this.selectedCountry?.code);
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  registerOnChange(fn: () => void) {
    this.addressForm.valueChanges.pipe(untilDestroyed(this)).subscribe(fn);
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  writeValue(value: AddressAttributes) {
    if (value) {
      const saneValue = this.sanitizeValue(value);
      this.addressForm.patchValue(saneValue, { emitEvent: false });
    } else {
      this.addressForm.reset();
    }

    this.updateCountryAndProvince();
    this.addressForm.updateValueAndValidity();
  }

  setValidators() {
    if (!this.addressForm) {
      return;
    }

    const requiredFields = ['address1', 'city', 'postalCode', 'country', 'province'];
    const validator = this.addressRequired ? Validators.required : null;

    requiredFields.forEach((fieldName) => {
      const field = this.addressForm.get(fieldName);

      field.setValidators(validator);
      field.updateValueAndValidity();
    });
  }

  validate(): ValidationErrors {
    const { country, province } = this.addressForm.value;
    const invalid = { address: { valid: false, message: 'Fields Required.' } };
    // {} is a valid value for required, lets make sure we have code values
    if (this.addressRequired && (!country?.code || !province?.code)) {
      return invalid;
    }
    return this.addressForm.valid ? null : invalid;
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.addressForm.disable();
      this.countrySelect.setDisabledState(true);
      this.provinceSelect.setDisabledState(true);
    } else {
      this.addressForm.enable();
      this.countrySelect.setDisabledState(false);
      this.provinceSelect.setDisabledState(false);
    }
  }

  private sanitizeValue(value: AddressAttributes): AddressAttributes {
    return {
      ...pluck(value, 'address1', 'address2', 'city', 'postalCode'),
      country: { code: value?.country?.code },
      province: { code: value?.province?.code }
    };
  }
}
